import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  C, PageHeader, GlassCard, Badge, Skeleton, StatusDot, Sparkline, EmptyState, Input, Button,
} from "@coremonit/design-system";
import { Container as ContainerIcon, Search, EyeOff, Eye, Cpu, MemoryStick, HardDrive, Wifi, Copy } from "lucide-react";
import { useServices, useServicePods } from "../api/hooks";
import type { Pod } from "@coremonit/types";
import { toast } from "sonner";

export function ContainersPage() {
  const services = useServices();
  const [serviceId, setServiceId] = useState<string | undefined>();
  const pods = useServicePods(serviceId);
  const [selectedPod, setSelectedPod] = useState<Pod | null>(null);
  const [showSecrets, setShowSecrets] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<ContainerIcon size={16} />} title="Container Explorer" subtitle="Pods, sidecars, volumes, env, secrets (masked), live resource usage." />
      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: C.textMuted, fontSize: 11 }}>Service</span>
          <select
            value={serviceId ?? ""}
            onChange={(e) => { setServiceId(e.target.value || undefined); setSelectedPod(null); }}
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "6px 10px", fontSize: 12, minWidth: 260 }}
          >
            <option value="">Select a service…</option>
            {services.data?.items.map((s) => <option key={s.id} value={s.id} style={{ background: C.surface }}>{s.name}</option>)}
          </select>
        </div>
      </GlassCard>

      {!serviceId ? (
        <GlassCard style={{ padding: 40 }}>
          <EmptyState icon={<ContainerIcon size={22} />} title="Pick a service" description="Choose a service above to see its pods, containers, volumes, and live resource usage." />
        </GlassCard>
      ) : pods.isLoading ? (
        <Skeleton height={240} radius={16} />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 12, minHeight: 500 }}>
          {/* Pod list */}
          <GlassCard style={{ padding: 12, maxHeight: 640, overflow: "auto" }}>
            <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Pods ({pods.data?.pods.length ?? 0})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {pods.data?.pods.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedPod(p)}
                  style={{
                    textAlign: "left",
                    padding: 10,
                    background: selectedPod?.id === p.id ? C.blueDim : C.surfaceHigh,
                    border: `1px solid ${selectedPod?.id === p.id ? C.borderBright : C.border}`,
                    borderRadius: 10,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <StatusDot status={p.status === "running" ? "healthy" : p.status === "pending" ? "warning" : "critical"} />
                      <span style={{ color: C.text, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                    </div>
                    <Badge color={p.status === "running" ? "green" : p.status === "pending" ? "amber" : "red"}>{p.status}</Badge>
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 9, marginTop: 4 }}>{p.node}</div>
                </button>
              ))}
            </div>
          </GlassCard>

          {/* Pod detail */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {!selectedPod ? (
              <GlassCard style={{ padding: 40 }}>
                <EmptyState icon={<ContainerIcon size={22} />} title="Select a pod" description="Click a pod on the left to see its containers, volumes, and live gauges." />
              </GlassCard>
            ) : (
              <>
                <GlassCard style={{ padding: 14 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                    <Info label="Pod"      value={selectedPod.name} />
                    <Info label="Namespace" value={selectedPod.namespace} />
                    <Info label="Node"     value={selectedPod.node} />
                    <Info label="Image"    value={selectedPod.image} mono />
                    <Info label="Digest"   value={selectedPod.imageDigest.slice(0, 24) + "…"} mono />
                    <Info label="Uptime"   value={`${Math.round(selectedPod.uptimeSec / 60)} min`} />
                    <Info label="Restarts" value={String(selectedPod.restartCount)} color={selectedPod.restartCount > 3 ? C.red : C.text} />
                  </div>
                </GlassCard>

                <GlassCard style={{ padding: 14 }}>
                  <SectionTitle title="Containers (live)" />
                  <ContainerRows pod={selectedPod} kind="containers" serviceId={serviceId} />
                  {selectedPod.initContainers.length > 0 && (<>
                    <SectionTitle title="Init containers" />
                    <ContainerRows pod={selectedPod} kind="init" serviceId={serviceId} />
                  </>)}
                  {selectedPod.sidecars.length > 0 && (<>
                    <SectionTitle title="Sidecars" />
                    <ContainerRows pod={selectedPod} kind="sidecar" serviceId={serviceId} />
                  </>)}
                </GlassCard>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <GlassCard style={{ padding: 14 }}>
                    <SectionTitle title={`Volumes (${selectedPod.volumes.length})`} />
                    {selectedPod.volumes.map((v) => (
                      <div key={v.name} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
                        <span style={{ color: C.text }}>{v.name}</span>
                        <span style={{ color: C.textMuted, fontFamily: "ui-monospace, monospace" }}>{v.mountPath}</span>
                      </div>
                    ))}
                  </GlassCard>
                  <GlassCard style={{ padding: 14 }}>
                    <SectionTitle title={`Config maps (${selectedPod.configMaps.length})`} />
                    {selectedPod.configMaps.map((c) => (
                      <div key={c} style={{ padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.text }}>{c}</div>
                    ))}
                  </GlassCard>
                </div>

                <GlassCard style={{ padding: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <SectionTitle title={`Environment variables (${selectedPod.env.length})`} />
                    <Button variant="ghost" size="xs" icon={showSecrets ? <EyeOff size={11} /> : <Eye size={11} />} onClick={() => setShowSecrets((v) => !v)}>
                      {showSecrets ? "Hide secrets" : "Show secrets"}
                    </Button>
                  </div>
                  <div style={{ maxHeight: 240, overflow: "auto" }}>
                    {selectedPod.env.map((e) => (
                      <div key={e.key} style={{ display: "grid", gridTemplateColumns: "220px 1fr auto", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
                        <span style={{ color: C.blue, fontFamily: "ui-monospace, monospace" }}>{e.key}</span>
                        <span style={{ color: C.textSub, fontFamily: "ui-monospace, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {e.value ?? (showSecrets ? `<${e.fromSecret}>` : "•••••••••••")}
                        </span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(e.value ?? e.fromSecret ?? ""); toast.success("Copied"); }}
                          style={{ background: "transparent", border: "none", color: C.textMuted, cursor: "pointer" }}
                        ><Copy size={10} /></button>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>{title}</div>;
}

function ContainerRows({ pod, kind, serviceId }: { pod: Pod; kind: "containers" | "init" | "sidecar"; serviceId?: string }) {
  const nav = useNavigate();
  const rows = kind === "containers" ? pod.containers : kind === "init" ? pod.initContainers : pod.sidecars;
  const spark = (n: number) => Array.from({ length: 12 }, () => n * (0.6 + Math.random() * 0.6));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((c) => (
        <div key={c.name} style={{ padding: 10, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <button
                onClick={() => serviceId && nav(`/containers/${serviceId}/${pod.id}/${encodeURIComponent(c.name)}`)}
                style={{ background: "transparent", border: "none", padding: 0, cursor: serviceId ? "pointer" : "default", textAlign: "left" }}
              >
                <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{c.name}</div>
              </button>
              <div style={{ color: C.textMuted, fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{c.image}</div>
            </div>
            <Badge color={c.status === "running" ? "green" : c.status === "waiting" ? "amber" : "red"}>{c.status}</Badge>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            <Metric icon={<Cpu size={11} />} label="CPU"    value={`${c.cpuMillicoresLive}m`}  color={C.blue}   spark={spark(c.cpuMillicoresLive)} />
            <Metric icon={<MemoryStick size={11} />} label="Memory" value={`${c.memoryMbLive}Mi`} color={C.purple} spark={spark(c.memoryMbLive)} />
            <Metric icon={<HardDrive size={11} />} label="Disk" value={`${c.diskMbLive}Mi`} color={C.cyan} spark={spark(c.diskMbLive)} />
            <Metric icon={<Wifi size={11} />} label="Net" value={`${(c.networkKbpsLive / 1000).toFixed(1)}Mbps`} color={C.green} spark={spark(c.networkKbpsLive)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 6, fontSize: 10, color: C.textMuted }}>
            <span>FDs: <span style={{ color: C.text }}>{c.openFds}</span></span>
            <span>Threads: <span style={{ color: C.text }}>{c.threads}</span></span>
            <span>GC: <span style={{ color: C.text }}>{c.gcPct}%</span></span>
            <span>Heap: <span style={{ color: C.text }}>{c.heapMb}MB</span></span>
          </div>
        </div>
      ))}
    </div>
  );
}

function Metric({ icon, label, value, color, spark }: { icon: React.ReactNode; label: string; value: string; color: string; spark: number[] }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, marginBottom: 4 }}>{icon}{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ color, fontSize: 12, fontWeight: 700 }}>{value}</span>
        <Sparkline data={spark} color={color} width={60} height={18} strokeWidth={1} />
      </div>
    </div>
  );
}

function Info({ label, value, color = C.text, mono = false }: { label: string; value: string; color?: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>{label}</div>
      <div style={{ color, fontSize: 11, marginTop: 3, wordBreak: "break-word", fontFamily: mono ? "ui-monospace, monospace" : undefined }}>{value}</div>
    </div>
  );
}
